import { useState } from 'react';
import { Box } from '@chakra-ui/react';
import {
  Button,
  ErrorMessage,
  HStack,
  Heading,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import { LockIcon } from 'lucide-react';
import { format } from 'date-fns';
import { LabBillingType, LabOption, LabVendor } from '../../../api/types';
import { useAuth } from '../../../hooks';
import useCreateLabOrder from '../../../api/healthCoach/mutations/useCreateLabOrder';
import { SegmentedControl } from './SegmentedControl';
import { LabTestPicker } from './LabTestPicker';

type Props = {
  userId: string;
  onClose: () => void;
};

const BILLING_OPTIONS = [
  { value: LabBillingType.Insurance, label: 'Insurance' },
  { value: LabBillingType.SelfPay, label: 'Self-pay' },
];

const VENDOR_OPTIONS = [
  { value: LabVendor.Quest, label: 'Quest' },
  { value: LabVendor.Labcorp, label: 'Labcorp' },
];

const Caption = ({ children }: { children: string }) => (
  <Text
    fontSize={'2xs'}
    fontWeight={700}
    color={'bluegrey'}
    textTransform={'uppercase'}
    letterSpacing={'0.04em'}
  >
    {children}
  </Text>
);

// Reviewing provider and order date are set by the backend on submit, so they
// show here as read-only, matching the mockup.
const LockedField = ({ label, value }: { label: string; value: string }) => (
  <VStack align={'stretch'} gap={1}>
    <Caption>{label}</Caption>
    <HStack
      justify={'space-between'}
      align={'center'}
      borderWidth={1}
      borderColor={'cloud.200'}
      rounded={'lg'}
      bg={'cloud.50'}
      px={3}
      py={2}
    >
      <Text fontSize={'sm'} fontWeight={600} color={'bluegrey'}>
        {value}
      </Text>
      <Box as={'span'} color={'bluegrey'} display={'inline-flex'}>
        <LockIcon size={14} />
      </Box>
    </HStack>
  </VStack>
);

export const LabOrderCreateForm = ({ userId, onClose }: Props) => {
  // The reviewing provider is whoever is logged in: the backend sets them as
  // the orderer on submit.
  const { user } = useAuth();

  const [billingType, setBillingType] = useState<LabBillingType>(
    LabBillingType.Insurance
  );
  const [vendor, setVendor] = useState<LabVendor>(LabVendor.Labcorp);
  const [selectedTests, setSelectedTests] = useState<LabOption[]>([]);

  const { mutation } = useCreateLabOrder();

  const handleSubmit = () => {
    if (selectedTests.length === 0) return;

    mutation.mutate(
      {
        userId,
        labOptions: selectedTests.map((test) => ({
          id: Number(test.id),
          name: test.name,
        })),
        billingType,
        labCompany: vendor,
      },
      { onSuccess: () => onClose() }
    );
  };

  return (
    <VStack align={'stretch'} gap={6} w={'100%'} maxW={'720px'}>
      <Heading size={'md'} color={'secondary.500'}>
        New lab order
      </Heading>

      <HStack gap={6} align={'flex-start'}>
        <VStack align={'stretch'} gap={1} flex={1}>
          <Caption>Billing type</Caption>
          <SegmentedControl
            options={BILLING_OPTIONS}
            value={billingType}
            onChange={setBillingType}
            ariaLabel={'Billing type'}
          />
        </VStack>
        <VStack align={'stretch'} gap={1} flex={1}>
          <Caption>Lab vendor</Caption>
          <SegmentedControl
            options={VENDOR_OPTIONS}
            value={vendor}
            onChange={(nextVendor) => {
              // Tests belong to one vendor, so drop the selection when the
              // vendor changes to keep the order to a single lab company.
              setVendor(nextVendor);
              setSelectedTests([]);
            }}
            ariaLabel={'Lab vendor'}
          />
        </VStack>
      </HStack>

      <LabTestPicker
        vendor={vendor}
        selected={selectedTests}
        onChange={setSelectedTests}
      />

      <HStack gap={6} align={'flex-start'}>
        <Box flex={1}>
          <LockedField
            label={'Reviewing provider'}
            value={user ? `${user.first_name} ${user.last_name}` : '—'}
          />
        </Box>
        <Box flex={1}>
          <LockedField
            label={'Order date'}
            value={format(new Date(), 'MMM d, yyyy')}
          />
        </Box>
      </HStack>

      {mutation.isError && (
        <ErrorMessage
          message={
            (mutation.error as Error)?.message ??
            'Something went wrong placing the order.'
          }
        />
      )}

      <HStack justify={'flex-end'} gap={2}>
        <Button variant={'link'} onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={selectedTests.length === 0 || mutation.isLoading}
        >
          Place order
        </Button>
      </HStack>
    </VStack>
  );
};

export default LabOrderCreateForm;
